import React, { useEffect, useState } from 'react';
import { Input, Label, Button, Form, FormFeedback, Spinner } from 'reactstrap';
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createSelector } from 'reselect';

import withRouter from "../../Components/Common/withRouter";
import { loginUser, socialLogin, resetLoginFlag } from "../../slices/thunks";
import { resendVerification } from "../../helpers/fakebackend_helper";
import AhAuthShell from "./AhAuthShell";
import GoogleSignInButton from "./GoogleSignInButton";

const Login = (props) => {
    const dispatch = useDispatch();

    const selectLayoutState = (state) => state;
    const loginpageData = createSelector(
        selectLayoutState,
        (state) => ({
            user: state.Account.user,
            error: state.Login.error,
            loading: state.Login.loading,
            errorMsg: state.Login.errorMsg,
        })
    );
    const { user, error, loading, errorMsg } = useSelector(loginpageData);

    const [userLogin, setUserLogin] = useState([]);
    const [passwordShow, setPasswordShow] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendMsg, setResendMsg] = useState("");
    const [resendErr, setResendErr] = useState("");

    useEffect(() => {
        if (user && user) {
            const updatedUserData = process.env.REACT_APP_DEFAULTAUTH === "firebase" ? user.multiFactor.user.email : user.email;
            const updatedUserPassword = process.env.REACT_APP_DEFAULTAUTH === "firebase" ? "" : user.confirm_password;
            setUserLogin({
                email: updatedUserData,
                password: updatedUserPassword
            });
        }
    }, [user]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: userLogin.email || '',
            password: userLogin.password || '',
        },
        validationSchema: Yup.object({
            email: Yup.string().required("Please enter your email"),
            password: Yup.string().required("Please enter your password"),
        }),
        onSubmit: (values) => {
            dispatch(loginUser(values, props.router.navigate));
        }
    });

    useEffect(() => {
        return () => { dispatch(resetLoginFlag()); };
    }, [dispatch]);

    const needsVerification = !!error && String(error).toLowerCase().includes("verify");

    const handleResend = async () => {
        setResendMsg(""); setResendErr("");
        if (!validation.values.email) {
            setResendErr("Enter your email above first.");
            return;
        }
        setResending(true);
        try {
            const res = await resendVerification(validation.values.email);
            setResendMsg((res && res.message) || "Verification email sent. Please check your inbox.");
        } catch (e) {
            setResendErr(typeof e === "string" ? e : (e && e.message) || "Failed to resend verification email.");
        } finally {
            setResending(false);
        }
    };

    document.title = "Sign in | ApiHub";

    return (
        <AhAuthShell
            eyebrow="WELCOME BACK"
            title="Sign in to ApiHub"
            subtitle="Use one key for every AI service."
            footer={<>Don't have an account? <Link to="/register">Create one</Link></>}
        >
            {needsVerification && (
                <div className="ah-notice" role="alert">
                    <div className="ah-notice-icon">
                        <i className="ri-mail-send-line"></i>
                    </div>
                    <div className="ah-notice-body">
                        <strong>Verify your email to sign in.</strong>
                        <div style={{ marginTop: 4, color: "oklch(0.82 0.06 65)" }}>
                            We sent a verification link to <strong style={{ color: "var(--ah-fg)" }}>{validation.values.email || "your inbox"}</strong>. Click the link in that email, then try signing in again.
                        </div>
                        <div className="ah-notice-actions">
                            <Button color="warning" onClick={handleResend} disabled={resending}>
                                {resending ? <><Spinner size="sm" className="me-2" />Sending…</> : <><i className="ri-mail-send-line me-1" />Resend verification email</>}
                            </Button>
                        </div>
                        {resendMsg && (
                            <div style={{ marginTop: 10, fontSize: 12.5, color: "oklch(0.82 0.16 145)" }}>
                                <i className="ri-checkbox-circle-line me-1" />{resendMsg}
                            </div>
                        )}
                        {resendErr && (
                            <div style={{ marginTop: 10, fontSize: 12.5, color: "oklch(0.78 0.18 25)" }}>
                                <i className="ri-error-warning-line me-1" />{resendErr}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {error && !needsVerification && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: 20 }}>
                    {typeof error === "string" ? error : (error && error.message) || "Sign in failed."}
                </div>
            )}

            <div style={{ marginBottom: 16 }}>
                <GoogleSignInButton label="Sign in with Google" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 16px", color: "var(--ah-fg-dim)", fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: "var(--ah-border)" }} />
                <span>or continue with email</span>
                <div style={{ flex: 1, height: 1, background: "var(--ah-border)" }} />
            </div>

            <Form
                onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}
                action="#"
            >
                <div className="mb-3">
                    <Label htmlFor="email" className="form-label">Email</Label>
                    <Input
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="you@example.com"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={validation.touched.email && validation.errors.email ? true : false}
                    />
                    {validation.touched.email && validation.errors.email && (
                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    )}
                </div>

                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Label htmlFor="password-input" className="form-label mb-0">Password</Label>
                        <Link to="/forgot-password" style={{ fontSize: 12.5, color: "var(--ah-fg-muted)", textDecoration: "none" }}>Forgot?</Link>
                    </div>
                    <div className="position-relative auth-pass-inputgroup">
                        <Input
                            id="password-input"
                            name="password"
                            value={validation.values.password || ""}
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5"
                            placeholder="Enter password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.password && validation.errors.password ? true : false}
                        />
                        <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none"
                            onClick={() => setPasswordShow(!passwordShow)}
                            tabIndex={-1}
                            aria-label="Toggle password visibility"
                        >
                            <i className={passwordShow ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                        {validation.touched.password && validation.errors.password && (
                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                        )}
                    </div>
                </div>

                <div className="mt-4">
                    <Button color="primary" disabled={!!loading} className="w-100" type="submit">
                        {loading ? <><Spinner size="sm" className="me-2" />Signing in…</> : "Sign in →"}
                    </Button>
                </div>
            </Form>
        </AhAuthShell>
    );
};

export default withRouter(Login);
